import { ICartEntry } from '@rbi-ctg/menu';
import { IRewardFragment } from 'generated/graphql-gateway';
import { useLoyaltyRewardsList } from 'hooks/use-loyalty-rewards-list';
import { useLoyaltyContext } from 'state/loyalty';

const DEFAULT_STATE = {
  rewardRedeemable: false,
  incentiveNotInMenu: false,
  redeemRewardLoading: false,
  hasSufficientBalance: false,
  limitPerOrderMet: false,
  rewardPointCost: 0,
};

export const useRedeemRewardState = ({
  cartId,
  quantity,
  reward,
}: {
  cartId?: string;
  quantity: number;
  reward?: IRewardFragment;
}) => {
  const {
    stagedCartPoints,
    appliedLoyaltyRewards,
    totalTimesRewardApplied,
    loyaltyEnabled,
  } = useLoyaltyContext();
  const { sanityRewardsMap, loading } = useLoyaltyRewardsList();

  if (!cartId || !reward || !loyaltyEnabled) {
    return DEFAULT_STATE;
  }

  const numberOfTimesRewardApplied = appliedLoyaltyRewards[cartId]?.timesApplied;

  const { value: rewardId } =
    reward?.metadata?.find(metadata => metadata?.key === 'externalReferenceId') || {};

  const { incentiveNotInMenu } = sanityRewardsMap?.[rewardId] || {};

  const { pointCost, id } = reward;

  const maxRedemptionsMet = numberOfTimesRewardApplied >= quantity;

  const limitPerOrderMet =
    !!reward.limitPerOrder && totalTimesRewardApplied[id] === reward.limitPerOrder;

  const hasSufficientBalance = pointCost && stagedCartPoints >= pointCost;

  const rewardRedeemable = hasSufficientBalance && !maxRedemptionsMet && !limitPerOrderMet;

  return {
    rewardRedeemable,
    incentiveNotInMenu: Boolean(incentiveNotInMenu),
    redeemRewardLoading: loading,
    hasSufficientBalance,
    limitPerOrderMet,
    rewardPointCost: pointCost,
  };
};

export const useRedeemReward = (item?: ICartEntry) => {
  const { getAvailableRewardFromCartEntry } = useLoyaltyContext();
  const reward = item && getAvailableRewardFromCartEntry(item);

  return useRedeemRewardState({ cartId: item?.cartId, quantity: item?.quantity || 0, reward });
};
